import classNames from "classnames";
import { Field, FormikTouched } from "formik";
import { omit } from "lodash";

import { Accession, Case, Location } from "pwapi";

import { UserLocation } from "custom-models";

import useLocationInputs from "shared/hooks/useLocationInputs";
import { formatPhone, formatSSN } from "shared/utils/patientUtils";

import { Input } from "ui/accessioning/AccessionForm";
import useUserInformationInputs from "ui/CaseFormInputs/useUserInformationInputs";

import FormHeaders from "./FormHeaders";

export const checkGuarantorAddress = (address?: UserLocation) => {
  return address &&
    Object.keys(address).filter(
      (key) => key !== "id" && address[key as keyof UserLocation]
    ).length > 0
    ? address
    : undefined;
};

const PatientGuarantorForm = ({
  billingType,
  onHandlePatchAccessionGuarantor,
  onHandleChangeRelationship,
  onSetGuarantorValues,
  guarantorInputPrefix,
  displaySubmit,
  onHandleDiscardChanges,
  dirty,
  values,
  patientAddressValues,
  thirdPartyRequireLocationFields = false,
  touched = {},
  warnings = {},
}: {
  billingType?: Case.billing_type | null;
  onHandlePatchAccessionGuarantor?: Function;
  onHandleChangeRelationship?: Function;
  onSetGuarantorValues?: Function;
  guarantorInputPrefix?: string;
  displaySubmit?: boolean;
  dirty?: boolean;
  onHandleDiscardChanges?: Function;
  values: Accession["guarantor"];
  patientAddressValues?: Accession["address"];
  thirdPartyRequireLocationFields?: boolean;
  touched?: any;
  warnings?: any;
}) => {
  const guarantorInputs = useUserInformationInputs(
    "guarantor",
    false,
    guarantorInputPrefix || undefined
  );
  const addressInputs = useLocationInputs(
    "guarantor_location",
    `${guarantorInputPrefix ? `${guarantorInputPrefix}.` : ""}address`
  );

  const requireLocationFields = thirdPartyRequireLocationFields
    ? billingType === Case.billing_type.GLOBAL
    : false;
  const disableInputs = values?.relationship_to_patient === "SELF";

  // Unpack touched address fields for typescript
  const addressTouched: FormikTouched<Location> | undefined = touched
    .guarantor?.[0]?.address as unknown as FormikTouched<Location>;

  // Determine if any address fields have been touched for universal address warning
  const isAddressTouched =
    addressTouched?.street ||
    addressTouched?.street2 ||
    addressTouched?.city ||
    addressTouched?.state ||
    addressTouched?.zipcode;

  const addressWarning = warnings["guarantor[0]"]?.address;

  return (
    <>
      <FormHeaders
        title="Patient Guarantor"
        dirty={dirty}
        displaySubmit={displaySubmit}
        touched={
          isAddressTouched &&
          (values?.relationship_to_patient === "SPOUSE" ||
            values?.relationship_to_patient === "PARENT")
            ? { address: !!addressTouched }
            : {}
        }
        warnings={addressWarning ? { address: addressWarning } : {}}
        forms={["guarantor.address"]}
        formWarnings={["address"]}
        additionalControls={
          <button
            type="button"
            disabled={
              values?.relationship_to_patient === "SELF" ||
              !patientAddressValues ||
              !Object.keys(patientAddressValues).filter((s) => s).length
            }
            onClick={() => {
              const tempAddress = {
                street: patientAddressValues?.street || "",
                street2: patientAddressValues?.street2 || "",
                city: patientAddressValues?.city || "",
                state: patientAddressValues?.state || "",
                zipcode: patientAddressValues?.zipcode || "",
                phone1: values?.address?.phone1 || undefined,
                phone2: values?.address?.phone2 || undefined,
              };
              onHandlePatchAccessionGuarantor &&
                onHandlePatchAccessionGuarantor({
                  ...values,
                  address: checkGuarantorAddress(tempAddress) || null,
                });
              onSetGuarantorValues &&
                onSetGuarantorValues({
                  ...values,
                  address: checkGuarantorAddress(tempAddress) || null,
                });
            }}
          >
            Use Patient Address
          </button>
        }
        onHandleDiscardChanges={onHandleDiscardChanges}
      />
      <div className="LISForm__section">
        <div className="Accession__input Input--1">
          <label>Sub Relationship</label>
          <Field
            {...omit(guarantorInputs.subRelationship, "defaultOptionLabel")}
            as="select"
            style={{ width: "100%" }}
            onBlur={
              onHandlePatchAccessionGuarantor &&
              (() => {
                onHandlePatchAccessionGuarantor(values);
              })
            }
            onChange={(e: any) => {
              onHandleChangeRelationship &&
                onHandleChangeRelationship(e.target.value);
            }}
          >
            <option value="">
              {guarantorInputs.subRelationship.defaultOptionLabel}
            </option>
            {guarantorInputs.subRelationship.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Field>
        </div>
        <Input
          {...guarantorInputs.lastName}
          className={classNames("Input--2", {
            "Accession__input--disabled": disableInputs,
          })}
          handleOnBlur={
            onHandlePatchAccessionGuarantor &&
            (() => {
              onHandlePatchAccessionGuarantor(values);
            })
          }
          disabled={disableInputs}
        />
        <Input
          {...guarantorInputs.firstName}
          className={classNames("Input--2", {
            "Accession__input--disabled": disableInputs,
          })}
          handleOnBlur={
            onHandlePatchAccessionGuarantor &&
            (() => {
              onHandlePatchAccessionGuarantor(values);
            })
          }
          disabled={values?.relationship_to_patient === "SELF"}
        />
        <Input
          {...guarantorInputs.middleInitial}
          className={classNames("Input--1", {
            "Accession__input--disabled": disableInputs,
          })}
          handleOnBlur={
            onHandlePatchAccessionGuarantor &&
            (() => {
              onHandlePatchAccessionGuarantor(values);
            })
          }
          disabled={values?.relationship_to_patient === "SELF"}
        />
        <Input
          {...guarantorInputs.dateOfBirth}
          className={classNames("Input--2", {
            "Accession__input--disabled": disableInputs,
          })}
          handleOnBlur={
            onHandlePatchAccessionGuarantor &&
            (() => {
              onHandlePatchAccessionGuarantor({
                ...values,
                date_of_birth: values.date_of_birth || null,
              });
            })
          }
          disabled={values?.relationship_to_patient === "SELF"}
        />
        <div
          className={classNames("Accession__input", "Input--2", {
            "Accession__input--disabled": disableInputs,
          })}
        >
          <label>Sex</label>
          <Field
            {...omit(guarantorInputs.sex, "defaultOptionLabel")}
            as="select"
            style={{ width: "100%" }}
            onBlur={
              onHandlePatchAccessionGuarantor &&
              (() => {
                onHandlePatchAccessionGuarantor(values);
              })
            }
            disabled={values?.relationship_to_patient === "SELF"}
          >
            <option value="">{guarantorInputs.sex.defaultOptionLabel}</option>
            {guarantorInputs.sex.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Field>
        </div>
        <Input
          {...guarantorInputs.ssn}
          className={classNames("Input--2", {
            "Accession__input--disabled": disableInputs,
          })}
          handleOnBlur={
            onHandlePatchAccessionGuarantor &&
            (() => {
              onHandlePatchAccessionGuarantor(values);
            })
          }
          formatter={formatSSN}
          disabled={values?.relationship_to_patient === "SELF"}
        />
        <Input
          {...addressInputs.street}
          className={classNames("Input--6", {
            "Accession__input--disabled": disableInputs,
          })}
          handleOnBlur={
            onHandlePatchAccessionGuarantor &&
            (() => {
              onHandlePatchAccessionGuarantor(values);
            })
          }
          required={requireLocationFields && !disableInputs}
          disabled={values?.relationship_to_patient === "SELF"}
        />
        <Input
          {...addressInputs.street2}
          className={classNames("Input--6", {
            "Accession__input--disabled": disableInputs,
          })}
          handleOnBlur={
            onHandlePatchAccessionGuarantor &&
            (() => {
              onHandlePatchAccessionGuarantor(values);
            })
          }
          disabled={values?.relationship_to_patient === "SELF"}
        />
        <Input
          {...addressInputs.city}
          className={classNames({
            "Accession__input--disabled": disableInputs,
          })}
          handleOnBlur={
            onHandlePatchAccessionGuarantor &&
            (() => {
              onHandlePatchAccessionGuarantor(values);
            })
          }
          disabled={values?.relationship_to_patient === "SELF"}
        />
        <div
          className={classNames("Accession__input", "Input--1", {
            "Accession__input--disabled": disableInputs,
          })}
        >
          <label>State</label>
          <Field
            {...omit(addressInputs.state, "defaultOptionLabel")}
            as="select"
            style={{ width: "100%" }}
            onBlur={
              onHandlePatchAccessionGuarantor &&
              (() => {
                onHandlePatchAccessionGuarantor(values);
              })
            }
            disabled={values?.relationship_to_patient === "SELF"}
          >
            <option value="">{addressInputs.state.defaultOptionLabel}</option>
            {addressInputs.state.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Field>
        </div>
        <Input
          {...addressInputs.zipcode}
          className={classNames("Input--2", {
            "Accession__input--disabled": disableInputs,
          })}
          handleOnBlur={
            onHandlePatchAccessionGuarantor &&
            (() => {
              onHandlePatchAccessionGuarantor(values);
            })
          }
          required={requireLocationFields && !disableInputs}
          disabled={values?.relationship_to_patient === "SELF"}
        />
        <Input
          {...addressInputs.phone1}
          className={classNames("Input--3", {
            "Accession__input--disabled": disableInputs,
          })}
          handleOnBlur={
            onHandlePatchAccessionGuarantor &&
            (() => {
              onHandlePatchAccessionGuarantor(values);
            })
          }
          required={requireLocationFields && !disableInputs}
          disabled={values?.relationship_to_patient === "SELF"}
          formatter={formatPhone}
        />
        <Input
          {...addressInputs.phone2}
          className={classNames("Input--3", {
            "Accession__input--disabled": disableInputs,
          })}
          handleOnBlur={
            onHandlePatchAccessionGuarantor &&
            (() => {
              onHandlePatchAccessionGuarantor(values);
            })
          }
          disabled={values?.relationship_to_patient === "SELF"}
          formatter={formatPhone}
        />
      </div>
    </>
  );
};

export default PatientGuarantorForm;
